import React from 'react';
import { View, Pressable, useWindowDimensions } from 'react-native';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ThemedText } from '@/components/themed-text';
import { Logo } from '@/components/logo';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

const CHECKOUT_REDIRECT_KEY = 'controlapag_checkout_redirect';

// Função para salvar o redirect no storage
export function saveCheckoutRedirect(chargeId: string) {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(CHECKOUT_REDIRECT_KEY, chargeId);
  }
}

// Função para obter e limpar o redirect do storage
export function getAndClearCheckoutRedirect(): string | null {
  if (typeof window !== 'undefined' && window.localStorage) {
    const chargeId = window.localStorage.getItem(CHECKOUT_REDIRECT_KEY);
    if (chargeId) {
      window.localStorage.removeItem(CHECKOUT_REDIRECT_KEY);
    }
    return chargeId;
  }
  return null;
}

// Função para verificar se existe um redirect pendente
export function hasCheckoutRedirect(): boolean {
  if (typeof window !== 'undefined' && window.localStorage) {
    return !!window.localStorage.getItem(CHECKOUT_REDIRECT_KEY);
  }
  return false;
}

type WelcomeModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chargeId: string;
  providerName?: string;
};

export function WelcomeModal({ open, onOpenChange, chargeId, providerName }: WelcomeModalProps) {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const handleLogin = () => {
    saveCheckoutRedirect(chargeId);
    onOpenChange(false);
    router.push('/(auth)/login');
  };

  const handleRegister = () => {
    saveCheckoutRedirect(chargeId);
    onOpenChange(false);
    router.push('/(auth)/register?role=CLIENT');
  };

  const handleContinueWithoutAccount = () => {
    onOpenChange(false);
  };

  const contentClassName = isMobile
    ? 'w-full h-auto max-h-[90%] rounded-t-3xl'
    : 'w-[95%] max-w-lg';

  const benefits = [
    {
      icon: 'receipt-outline' as const,
      title: 'Histórico de pagamentos',
      description: 'Acompanhe todas as suas cobranças em um só lugar',
    },
    {
      icon: 'notifications-outline' as const,
      title: 'Notificações',
      description: 'Receba avisos de vencimentos e confirmações',
    },
    {
      icon: 'document-text-outline' as const,
      title: 'Comprovantes',
      description: 'Acesse seus comprovantes a qualquer momento',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={contentClassName}>
        {/* Botão de fechar (X) */}
        <Pressable
          onPress={handleContinueWithoutAccount}
          className="absolute right-4 top-4 z-10 rounded-full p-2 hover:bg-neutral-100"
        >
          <Ionicons className="text-neutral-500" name="close" size={24} />
        </Pressable>

        <DialogHeader className="items-center pr-10">
          <View className="mb-4">
            <Logo size={56} color="tint" />
          </View>
          <DialogTitle>
            <ThemedText className="text-center font-bold text-xl">
              Bem-vindo à Controlapag! 👋
            </ThemedText>
          </DialogTitle>
          <DialogDescription>
            <ThemedText className="mt-2 text-center text-sm text-foreground/60">
              {providerName
                ? `${providerName} está usando a Controlapag para gerenciar cobranças.`
                : 'Uma plataforma moderna para gerenciar serviços e pagamentos.'}
            </ThemedText>
          </DialogDescription>
        </DialogHeader>

        {/* Benefícios */}
        <View className="my-6 gap-4">
          <ThemedText className="text-center font-semibold text-sm text-foreground/80">
            Crie uma conta gratuita e tenha acesso a:
          </ThemedText>

          <View className="gap-3">
            {benefits.map((benefit, index) => (
              <View
                key={index}
                className="flex-row items-center gap-3 rounded-xl bg-neutral-50 p-3"
              >
                <View className="h-10 w-10 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
                  <Ionicons
                    className="text-primary dark:text-white"
                    name={benefit.icon}
                    size={20}
                  />
                </View>
                <View className="flex-1">
                  <ThemedText className="font-medium text-sm">{benefit.title}</ThemedText>
                  <ThemedText className="text-xs text-foreground/60">
                    {benefit.description}
                  </ThemedText>
                </View>
              </View>
            ))}
          </View>
        </View>

        <DialogFooter className="gap-3">
          <Button variant="default" onPress={handleRegister} className="flex-1">
            <View className="flex-row items-center justify-center gap-2">
              <Ionicons name="person-add-outline" size={18} color="white" />
              <ThemedText className="font-semibold text-white">Criar conta</ThemedText>
            </View>
          </Button>

          <Button variant="outline" onPress={handleLogin} className="flex-1">
            <View className="flex-row items-center justify-center gap-2">
              <Ionicons className="text-foreground" name="log-in-outline" size={18} />
              <ThemedText className="font-medium">Já tenho conta</ThemedText>
            </View>
          </Button>
        </DialogFooter>

        <Pressable onPress={handleContinueWithoutAccount} className="mt-4 items-center py-2">
          <ThemedText className="text-center text-sm text-foreground/50 underline">
            Continuar sem criar conta
          </ThemedText>
        </Pressable>
      </DialogContent>
    </Dialog>
  );
}
